import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useRef, useState, type FormEvent, type ReactElement } from "react";
import { ContentHeader, EmptyState, Modal, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type {
  CheckoutOpenResult,
  CheckoutOptions,
  CheckoutQueueItem,
  CheckoutQueueResult,
  CheckoutSettings,
} from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { formatDateBR, type CheckoutMode, type CheckoutSort } from "@cx/domain";
import { useSession } from "../../session";
import { CHECKOUT_MODE_LABEL, checkoutError, pickingIds, progressPct, qty } from "./labels";
import { SOCKET_STATUS_LABEL, useCheckoutSocket, useThrottled } from "./useCheckoutSocket";

/** Checkout de pedidos (RF-VEN-07): bipar o pedido (ou o produto) e a fila dos que aguardam conferência. */
export function CheckoutPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const canCreate = can(perms, "checkout", "create");
  // A configuração escolhe as situações do pedido: exige também sales_orders:update (como a API).
  const canConfigure = can(perms, "checkout", "update") && can(perms, "sales_orders", "update");
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState<CheckoutMode>("by_order");
  const [code, setCode] = useState("");
  const [statusId, setStatusId] = useState("");
  const [carrierId, setCarrierId] = useState("");
  const [sort, setSort] = useState<CheckoutSort>("oldest");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [configuring, setConfiguring] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);
  // A fila só recebe mudanças de estado (não cada bipagem); recarrega no máximo uma vez a cada 2 s.
  const refreshQueue = useThrottled(
    () => void qc.invalidateQueries({ queryKey: ["checkout", companyId, "queue"] }),
  );
  const live = useCheckoutSocket(companyId, refreshQueue);

  const options = useQuery({
    queryKey: ["checkout", companyId, "options"],
    queryFn: () => api<CheckoutOptions>("/checkout/options"),
  });
  const queue = useInfiniteQuery({
    queryKey: ["checkout", companyId, "queue", statusId, carrierId, sort],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<CheckoutQueueResult>(
        `/checkout/orders?${new URLSearchParams({
          sort,
          ...(statusId ? { statusId } : {}),
          ...(carrierId ? { carrierId } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = queue.data?.pages.flatMap((p) => p.data) ?? [];
  const count = queue.data?.pages[0]?.summary.count;
  const statusById = new Map((options.data?.statuses ?? []).map((s) => [s.id, s]));
  const sourceStatuses = (options.data?.settings.sourceStatusIds ?? []).flatMap((id) => {
    const s = statusById.get(id);
    return s ? [s] : [];
  });

  const open = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<CheckoutOpenResult>("/checkout/sessions", {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify(body),
      }),
    onSuccess: (r) => {
      setCode("");
      qc.invalidateQueries({ queryKey: ["checkout", companyId] });
      if (r.finish) {
        toast("success", `Pedido conferido (${r.read?.productName ?? "produto"}).`);
        codeRef.current?.focus();
        return;
      }
      if (r.read) toast("success", `${r.read.productName}: lido.`);
      navigate({ to: "/vendas/checkout/$id", params: { id: r.sessionId } });
    },
    onError: (e) => {
      toast("danger", checkoutError(e));
      codeRef.current?.select();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!code.trim() || open.isPending) return;
    open.mutate(
      mode === "by_order" ? { mode, code } : { mode, code, sort, ...(carrierId ? { carrierId } : {}) },
    );
  };
  const toggle = (r: CheckoutQueueItem) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(r.id)) next.delete(r.id);
      else next.add(r.id);
      return next;
    });

  return (
    <>
      <ContentHeader
        title="Checkout de pedidos"
        breadcrumb={["Vendas", "Checkout de pedidos"]}
        actions={
          <div className="d-flex gap-2">
            <Link
              to="/vendas/checkout/separacao"
              search={{ ids: selected.size ? pickingIds(selected) : undefined }}
              className="btn btn-outline-secondary"
            >
              Relatório de separação{selected.size ? ` (${selected.size})` : ""}
            </Link>
            {canConfigure && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setConfiguring(true)}
              >
                Configurar
              </button>
            )}
          </div>
        }
      />

      {canCreate && (
        <div className="box card mb-3">
          <form className="row g-2 p-3 align-items-end" onSubmit={submit} aria-label="Bipar">
            <div className="col-12 col-md-3">
              <label className="form-label small" htmlFor="ck-mode">
                Modo
              </label>
              <select
                id="ck-mode"
                className="form-select"
                value={mode}
                onChange={(e) => setMode(e.target.value as CheckoutMode)}
              >
                {(Object.keys(CHECKOUT_MODE_LABEL) as CheckoutMode[]).map((m) => (
                  <option key={m} value={m}>
                    {CHECKOUT_MODE_LABEL[m]}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label small" htmlFor="ck-code">
                {mode === "by_order"
                  ? "Pedido (número, nº na loja ou pedido do cliente)"
                  : "Produto (GTIN ou SKU)"}
              </label>
              <input
                id="ck-code"
                ref={codeRef}
                className="form-control"
                autoComplete="off"
                autoFocus
                maxLength={60}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-3">
              <button type="submit" className="btn btn-primary w-100" disabled={open.isPending}>
                {mode === "by_order" ? "Abrir conferência" : "Conferir"}
              </button>
            </div>
            {mode === "single_product" && (
              <p className="small text-muted mb-0">
                Confere o pedido {sort === "oldest" ? "mais antigo" : "mais recente"} da fila que tem só este
                produto; completo, ele é salvo na hora.
              </p>
            )}
          </form>
        </div>
      )}

      <div className="box card">
        <div className="row g-2 p-3 pb-0 align-items-end">
          <div className="col-12 col-md-3">
            <label className="form-label small" htmlFor="ck-status">
              Situação
            </label>
            <select
              id="ck-status"
              className="form-select"
              value={statusId}
              onChange={(e) => setStatusId(e.target.value)}
            >
              <option value="">Todas da fila</option>
              {sourceStatuses.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label small" htmlFor="ck-carrier">
              Logística
            </label>
            <select
              id="ck-carrier"
              className="form-select"
              value={carrierId}
              onChange={(e) => setCarrierId(e.target.value)}
            >
              <option value="">Todas</option>
              {(options.data?.carriers ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label small" htmlFor="ck-sort">
              Ordem
            </label>
            <select
              id="ck-sort"
              className="form-select"
              value={sort}
              onChange={(e) => setSort(e.target.value as CheckoutSort)}
            >
              <option value="oldest">Mais antigos primeiro</option>
              <option value="newest">Mais recentes primeiro</option>
            </select>
          </div>
          <div className="col-12 col-md-3 text-md-end small text-muted" role="status" aria-live="polite">
            {SOCKET_STATUS_LABEL[live]}
            {count !== undefined && count >= 0 && ` · ${count} pedido(s) na fila`}
          </div>
        </div>
        <QueryStatus loading={queue.isLoading} error={queue.error} />
        {queue.isSuccess && rows.length === 0 && (
          <EmptyState
            title="Nenhum pedido aguardando checkout"
            description="Pedidos nas situações da fila aparecem aqui."
            actions={[]}
          />
        )}
        {rows.length > 0 && (
          <div className="table-responsive mt-2">
            <table className="table align-middle mb-0" aria-label="Pedidos aguardando checkout">
              <thead>
                <tr>
                  <th scope="col" style={{ width: 40 }}>
                    <span className="visually-hidden">Selecionar</span>
                  </th>
                  <th scope="col">Nº</th>
                  <th scope="col">Data</th>
                  <th scope="col">Cliente</th>
                  <th scope="col">Logística</th>
                  <th scope="col" className="text-end">
                    Itens
                  </th>
                  <th scope="col">Situação</th>
                  <th scope="col">Conferência</th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        aria-label={`Selecionar pedido nº ${r.number}`}
                        checked={selected.has(r.id)}
                        onChange={() => toggle(r)}
                      />
                    </td>
                    <td>{r.number}</td>
                    <td>{formatDateBR(r.date)}</td>
                    <td>{r.contactName}</td>
                    <td>{r.carrierName ?? "—"}</td>
                    <td className="text-end">{r.itemsCount}</td>
                    <td>
                      <StatusBadge tone={r.status.color}>{r.status.label}</StatusBadge>
                    </td>
                    <td data-testid={`progress-${r.number}`}>
                      {r.session
                        ? `${qty(r.session.checkedQ4)} de ${qty(r.session.requiredQ4)} (${progressPct(r.session)}%)`
                        : "—"}
                    </td>
                    <td className="text-end">
                      {r.session ? (
                        <Link
                          to="/vendas/checkout/$id"
                          params={{ id: r.session.id }}
                          className="btn btn-sm btn-outline-primary"
                        >
                          Continuar
                        </Link>
                      ) : (
                        canCreate && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            disabled={open.isPending}
                            onClick={() => open.mutate({ mode: "by_order", salesOrderId: r.id })}
                          >
                            Conferir
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {queue.hasNextPage && (
              <div className="p-3 text-center">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={queue.isFetchingNextPage}
                  onClick={() => queue.fetchNextPage()}
                >
                  Carregar mais
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {options.data && (
        <SettingsDialog open={configuring} options={options.data} onClose={() => setConfiguring(false)} />
      )}
    </>
  );
}

/** Configuração: situações da fila, a de concluído e a do parcial (vazia = parcial desligado). */
function SettingsDialog({
  open,
  options,
  onClose,
}: {
  open: boolean;
  options: CheckoutOptions;
  onClose(): void;
}): ReactElement {
  const { companyId, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [value, setValue] = useState<CheckoutSettings>(options.settings);
  const [error, setError] = useState<string | null>(null);
  // Fila, concluído e parcial: só situações em andamento (o checkout nunca abre, conclui nem cancela o pedido).
  const inProgress = options.statuses.filter((s) => s.baseState === "in_progress");
  const save = useMutation({
    mutationFn: () => api("/checkout/settings", { method: "PUT", body: JSON.stringify(value) }),
    onSuccess: () => {
      toast("success", "Configuração do checkout salva");
      qc.invalidateQueries({ queryKey: ["checkout", companyId] });
      onClose();
    },
    onError: (e) => setError(checkoutError(e)),
  });
  const toggleSource = (id: string) =>
    setValue((v) => ({
      ...v,
      sourceStatusIds: v.sourceStatusIds.includes(id)
        ? v.sourceStatusIds.filter((x) => x !== id)
        : [...v.sourceStatusIds, id],
    }));
  return (
    <Modal
      open={open}
      title="Configurar checkout"
      onClose={onClose}
      footer={
        <button
          type="button"
          className="btn btn-primary"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          Salvar
        </button>
      }
    >
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      <fieldset className="mb-3">
        <legend className="form-label small">Situações da fila de checkout</legend>
        {inProgress.map((s) => (
          <div className="form-check" key={s.id}>
            <input
              id={`ck-src-${s.id}`}
              type="checkbox"
              className="form-check-input"
              checked={value.sourceStatusIds.includes(s.id)}
              onChange={() => toggleSource(s.id)}
            />
            <label className="form-check-label" htmlFor={`ck-src-${s.id}`}>
              {s.label}
            </label>
          </div>
        ))}
      </fieldset>
      <div className="mb-3">
        <label className="form-label small" htmlFor="ck-done">
          Situação ao concluir
        </label>
        <select
          id="ck-done"
          className="form-select"
          value={value.doneStatusId}
          onChange={(e) => setValue((v) => ({ ...v, doneStatusId: e.target.value }))}
        >
          {inProgress.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="form-label small" htmlFor="ck-partial">
          Situação do checkout parcial
        </label>
        <select
          id="ck-partial"
          className="form-select"
          value={value.partialStatusId ?? ""}
          onChange={(e) => setValue((v) => ({ ...v, partialStatusId: e.target.value || null }))}
        >
          <option value="">Não permitir checkout parcial</option>
          {inProgress.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
    </Modal>
  );
}
